import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { Home, User, Folder, Cpu, Mail, Rocket } from 'lucide-react';
import ThemeToggle from "./ThemeToggle";

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const mobileMenuRef = useRef(null);

  // Scroll for navbar style
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Click outside & scroll to close mobile menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };
    const closeOnScroll = () => setMobileMenuOpen(false);

    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', closeOnScroll);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', closeOnScroll);
    };
  }, [mobileMenuOpen]);

  const menuVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { staggerChildren: 0.1, when: "beforeChildren" }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0 }
  };

  const navItems = [
    { name: 'Home', icon: <Home size={16} className="mr-1" /> },
    { name: 'About', icon: <User size={16} className="mr-1" /> },
    { name: 'Projects', icon: <Folder size={16} className="mr-1" /> },
    { name: 'Skills', icon: <Cpu size={16} className="mr-1" /> },
    { name: 'Contact', icon: <Mail size={16} className="mr-1" /> },
  ];

  return (
    <motion.nav
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/80 dark:bg-black/80 backdrop-blur-lg py-3 shadow-sm' 
          : 'bg-transparent py-5'
      }`}
      initial={false}
      animate={{ padding: scrolled ? '0.75rem 0' : '1.25rem 0' }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
    >
      <div className="mx-auto max-w-6xl px-4 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full flex items-center justify-center text-white bg-gradient-to-r from-indigo-500 to-purple-500 shadow-md">
            <Rocket size={22} />
          </div>
          <a href="/" className="text-gray-900 dark:text-white text-lg font-medium tracking-wide" style={{ fontFamily: "Playwrite PL, cursive" }}>
            <span>Bouba</span><span className="text-indigo-600 dark:text-indigo-400">.Dev</span>
          </a>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-6">
          {navItems.map(({ name, icon }) => (
            <a
              key={name}
              href={`#${name.toLowerCase()}`}
              className="flex items-center gap-1 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
            >
              {icon}
              {name}
            </a>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <a
            download
            href="documents/Resume_Bouba_Ahmed.pdf"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-md hover:shadow-lg transition-all"
          >
            <DocumentTextIcon className="h-4 w-4" />
            Resume
          </a>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-md text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            ref={mobileMenuRef}
            className="md:hidden absolute left-4 right-4 top-20 bg-white/95 dark:bg-gray-900/95 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 overflow-hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <motion.div
              className="p-3 space-y-1"
              variants={menuVariants}
              initial="hidden"
              animate="visible"
            >
              {navItems.map(({ name, icon }) => (
                <motion.a
                  key={name}
                  href={`#${name.toLowerCase()}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                  variants={itemVariants}
                >
                  <span className="text-indigo-500 dark:text-indigo-400">{icon}</span>
                  {name}
                </motion.a>
              ))}
              <motion.a
                download
                href="documents/Resume_Bouba_Ahmed.pdf"
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
                variants={itemVariants}
              >
                <DocumentTextIcon className="h-5 w-5" />
                Download Resume
              </motion.a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;